import { Trans } from '@lingui/react'
import { useAtom } from 'jotai'

import { useAcceptPremiumAccountAgreementsMutation } from '@twisto/api-graphql'
import { FormSubmitButton } from '@twisto/components/atoms/button'
import type { CastedSubmitHandler } from '@twisto/components/atoms/form'
import { useForm } from '@twisto/components/atoms/form'
import {
  Headline,
  HeadlineIcon,
  HeadlineSubtitle,
  HeadlineTitle,
} from '@twisto/components/atoms/headline'
import {
  SelectableListItemLink,
  SelectableListItemText,
} from '@twisto/components/molecules/selectable-list'
import { useResponseErrors } from '@twisto/form'
import { BigContractIcon } from '@twisto/icons'

import { FlowForm, submittingFlowFormAtom } from '../../components'
import { useFlow } from '../../flow/flow.context'

import * as styles from './premium-account-agreements-step.styles'

type FormValues = Record<string, unknown>

export const PremiumAccountAgreementsStep = () => {
  const { flow, sessionId } = useFlow<'PremiumAccountAgreementsStep'>()
  const [submitting] = useAtom(submittingFlowFormAtom)
  const { documents } = flow.currentStep

  const formContext = useForm({
    mode: 'onSubmit',
    defaultValues: {},
  })

  const [acceptPremiumAccountAgreements, { data }] =
    useAcceptPremiumAccountAgreementsMutation()

  // handle field errors - such as invalid email
  useResponseErrors(data?.acceptPremiumAccountAgreements, formContext)

  const handleSubmit: CastedSubmitHandler<FormValues> = async () =>
    acceptPremiumAccountAgreements({
      variables: {
        auth: { sessionId },
      },
    })

  return (
    <>
      <Headline css={styles.headline}>
        <HeadlineIcon>
          <BigContractIcon color="primary" height="100%" width="100%" />
        </HeadlineIcon>
        <HeadlineTitle>
          <Trans id="flow.premiumAccountAgreementsStep.title" />
        </HeadlineTitle>
        <HeadlineSubtitle>
          <Trans id="flow.premiumAccountAgreementsStep.subtitle" />
        </HeadlineSubtitle>
      </Headline>
      <FlowForm {...formContext} onSubmit={handleSubmit}>
        <div css={styles.documents}>
          {documents.map(({ title, url }, index) => (
            <SelectableListItemLink key={index} href={url} target="__blank">
              <SelectableListItemText align="start">
                {title}
              </SelectableListItemText>
            </SelectableListItemLink>
          ))}
        </div>

        <FormSubmitButton disabled={submitting}>
          <Trans id="flow.premiumAccountAgreementsStep.submitButton" />
        </FormSubmitButton>
      </FlowForm>
    </>
  )
}
